import upperFirst from "lodash/upperFirst";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";

import { Text } from "@/components/atoms/Text";
import InstantBookIcon from "@/icons/__generated__/InstantBookFilled/InstantBookFilled24";
import { ListingData, useListingData } from "~/context/ListingPageDataContext";

import { getIsInstantBookOffered } from "../../book-listing-utils";
import { useBookListingContext } from "../../BookListingContext";
import { getFsvOverDurationState } from "../../fsv-over-duration.utils";
import { useFsvDepositLayout } from "../../hooks/use-fsv-deposit-layout";

import { FsvPriceFeeCalcSkeleton } from "./FsvPriceFeeCalcSkeleton";

/**
 * Fee-calc header for full-service venues: due today plus the food-and-drink
 * minimum on deposit listings.
 */
export const FsvBookListingCardHeader = () => {
  const { data: listingData = {} as ListingData } = useListingData();
  const { quote, isFetchingQuote, estimate, isFetchingEstimate } =
    useBookListingContext();
  const {
    depositBreakdown,
    foodDrinkCategoryPhrase,
    isDepositListing,
    showDepositLayout,
  } = useFsvDepositLayout();
  const intl = useIntl();

  const { guestPaymentDetails, masterContract } = quote || {};
  const { booking } = quote?.spaceRentalContract?.meta || {};

  const bookingTotal = guestPaymentDetails
    ? (guestPaymentDetails?.subtotal_remaining ?? 0)
    : (estimate?.total ?? 0);

  const prepaidMenuTotal = masterContract
    ? (masterContract.calculations?.guest?.pay_at_venue?.due_at_venue_amount ??
      0)
    : (estimate?.flat_rate_fandb_amount ?? 0);

  const showSpinner =
    isFetchingQuote || isFetchingEstimate || (!estimate && !quote);

  const { displayHours } = getFsvOverDurationState({
    isFullServiceVenue: listingData.fullService?.active,
    minBookingDurationHours:
      listingData.fullService?.min_booking_duration_hours,
    selectedDurationHours: booking?.duration,
  });

  const showInstantBook = getIsInstantBookOffered({ booking, listingData });

  return (
    <div className="tw-border-b tw-border-gray-200">
      <div className="tw-flex tw-flex-col tw-items-center tw-p-24">
        {showSpinner && (
          <FsvPriceFeeCalcSkeleton
            // Same check as showDepositLayout, minus the breakdown that is still loading
            isDepositListing={isDepositListing && !!foodDrinkCategoryPhrase}
          />
        )}

        {!showSpinner && depositBreakdown && foodDrinkCategoryPhrase && (
          <div className="tw-flex tw-flex-col tw-items-center tw-w-full">
            {showInstantBook && (
              <span
                className="tw-mb-8"
                data-testing-id="instantBookingLightningSign"
              >
                <InstantBookIcon />
              </span>
            )}
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4 tw-text-center tw-w-full">
              <Text
                typeface="body-1"
                element="div"
                className="tw-text-gray-600"
              >
                <FormattedMessage defaultMessage="Due today" />
              </Text>
              <Text
                typeface="headline-5-b"
                element="div"
                className="tw-text-black"
                data-testing-id="fsv-book-listing-card-header-price"
              >
                <FormattedNumber
                  value={Math.ceil(depositBreakdown.dueToday)}
                  style="currency"
                  currency={listingData.listingCurrency}
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </Text>
              <Text
                typeface="body-1"
                element="div"
                className="tw-text-gray-600"
                data-testing-id="fsv-book-listing-card-header-minimum"
              >
                <FormattedMessage
                  defaultMessage="{minSpend} {category} minimum"
                  values={{
                    category: upperFirst(foodDrinkCategoryPhrase),
                    minSpend: intl.formatNumber(
                      Math.ceil(depositBreakdown.minSpend),
                      {
                        style: "currency",
                        currency: listingData.listingCurrency,
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      },
                    ),
                  }}
                />
              </Text>
            </div>
          </div>
        )}

        {!showSpinner && !showDepositLayout && (
          <>
            <Text
              element="div"
              typeface="body-1"
              className="tw-text-gray-600 tw-mb-4"
            >
              <FormattedMessage defaultMessage="Due today" />
            </Text>

            <div className="tw-flex tw-items-center">
              {showInstantBook && (
                <span data-testing-id="instantBookingLightningSign">
                  <InstantBookIcon />
                </span>
              )}

              <Text
                typeface="headline-5-b"
                data-testing-id="fsv-book-listing-card-header-price"
              >
                <FormattedNumber
                  value={Math.ceil(bookingTotal)}
                  style="currency"
                  currency={listingData.listingCurrency}
                  minimumFractionDigits={0}
                  maximumFractionDigits={0}
                />
              </Text>
            </div>

            <div className="tw-flex tw-flex-col tw-items-center tw-mt-4 tw-text-center">
              <Text typeface="body-1-m" className="tw-text-purple-800 tw-mb-4">
                <FormattedMessage
                  defaultMessage="{minHours} hr reservation"
                  values={{
                    minHours: displayHours,
                  }}
                />
              </Text>
              <Text typeface="body-1">
                <FormattedMessage
                  defaultMessage="Requires {minSpend} minimum spend at venue"
                  values={{
                    minSpend: intl.formatNumber(prepaidMenuTotal, {
                      style: "currency",
                      currency: listingData.listingCurrency,
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }),
                  }}
                />
              </Text>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
